import React from 'react';
import { ICapsuleData } from "@sg/components/CapsuleBar/type.d";
import { getTableCapsuleConfig } from '@sg/utils/business';
import SpecialTableBar from '@sg/components/CapsuleBar/SpecialTableBar';
import Tag from '@sg/components/Tag';
import CommonBubble from '@/pages/home/components/CommonBubble';
import { localizeLabel } from '@sg/utils/translate';
import { rendTitle } from '@sg/utils/commonPageMethod';
import { toMillions, getYearPrefix } from '@/pages/strategic/utils/common';
import { Modal } from 'tea-component';
import PhaseCell from '@sg/components/PhaseCell';
import FollowerRange from '@sg/components/FollowerRange';
import DeleteButton from '@sg/components/DeleteButton';
import ExpandCollapseButton from '@sg/components/ExpandCollapseButton';
import InputNumberComponent from './components/InputNumberComponent';
import SustainabilitySelect from './components/SustainabilitySelect';
import { createBtnPoint, SGBuriedBtnPoint, MKTCostTab } from '@/pages/strategic/utils/buriedPoint';

export const getProjectColumns = (
    maxVsVallue: number,
    formatMessage: any,
    mktCostUpdateTime: string = '',
    options?: { isAllExpanded?: boolean; onToggleExpandAll?: () => void }
) => {
  // Extract year from mktCostUpdateTime (format: "2026-01")
  // Default to '25' if no valid year is found
  const yearPrefix = getYearPrefix(mktCostUpdateTime);

  return [
    {
      header: (
          <div>
              {options?.onToggleExpandAll ? (
                  <ExpandCollapseButton
                      isExpanded={!!options.isAllExpanded}
                      onClick={() => options.onToggleExpandAll!()}
                      onTrackToggle={(nextExpanded) => {
                        const btnPoint = createBtnPoint(
                          MKTCostTab,
                          'mktCostByProjectsCollapse',
                          nextExpanded
                            ? 'MKT Cost by Projects Expand All'
                            : 'MKT Cost by Projects Collapse All',
                        );
                        if (btnPoint) {
                          SGBuriedBtnPoint(btnPoint);
                        }
                      }}
                      title={formatMessage({ id: 'dbs_project' })}
                  />
              ) : (
                  <span style={{ fontWeight: 700 }}>
                      {formatMessage({ id: 'dbs_project' })}
                  </span>
              )}
          </div>
      ),
      dataIndex: 'Project',
      key: 'Project',
      width: 120,
      render: (cvm: any) => <span style={{ display: 'inline-block', fontWeight: cvm.isCategory ? 700 : 400, paddingLeft: cvm.isCategory ? '' : '22px' }}>{localizeLabel(cvm?.Project, formatMessage)}</span>
    },
    {
      header: rendTitle({ title: `${yearPrefix}A`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
      dataIndex: '25A',
      key: '25A',
      width: 70,
      render: (cvm: any) => (
        <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          {toMillions(cvm?.['25A'])}
        </div>
      )
    },
    {
      header: rendTitle({ title: `${yearPrefix}F`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
      dataIndex: '25F',
      key: '25F',
      width: 70,
      render: (cvm: any) => (
        <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          {toMillions(cvm?.['25F'])}
        </div>
      )
    },
    {
      header: rendTitle({ title: `${yearPrefix}B`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
      dataIndex: '25B',
      key: '25B',
      width: 70,
      render: (cvm: any) => (
        <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          {toMillions(cvm?.['25B'])}
        </div>
      )
    },
    {
      header: (
        <CommonBubble content={formatMessage({ id: 'dbs_mkt_25A_25B' })}>
          <div style={{ textAlign: 'center', textWrap: 'nowrap' }}>{yearPrefix}A / {yearPrefix}B</div>
        </CommonBubble>
      ),
      dataIndex: '25A VS 25B2',
      key: '25A VS 25B2',
      width: 140,
      render: (cvm: any) => {
        try {
          // Handle 0/0 case to avoid NaN
          const valueA = cvm?.['25A'] || 0;
          const valueB = cvm?.['25B'] || 0;

          // If both are 0, or B is 0, show 0%
          const dValue = valueB === 0 ? 0 : (valueA / valueB * 100);

          const newArr: ICapsuleData[] = getTableCapsuleConfig({ value: dValue, maxValue: maxVsVallue });
          return <div className={`${dValue === 0 ? 'dbs-table-default-gray' : cvm?.['Sustainability']} dbs-table-capsule dbs-table-capsule-positive`}
            style={{ marginLeft: dValue === 0 ? '20px' : '-20px' }}>
            <SpecialTableBar data={newArr} height={20} coefficient={1.5} />
          </div>
        } catch (error) {
          console.error(error);
        }
      }
    },
    {
      header: <CommonBubble content={formatMessage({ id: 'dbs_mkt_sustainability' })}>{formatMessage({ id: 'dbs_table_sustainability' })}</CommonBubble>,
      dataIndex: 'Sustainability',
      key: 'Sustainability',
      width: 100,
      render: (cvm: any) => {
        return cvm?.['Sustainability'] ? <Tag text={cvm?.['Sustainability']} type={cvm?.['Sustainability'] === 'Overconsumed' ? "warn" : 'default'} /> : null;
      },
    },
  ];
};

export const editProjectColums = (maxVsVallue: number, formatMessage: any, projectOptions: any[] = [], loading: boolean = false, mktCostUpdateTime: string = '') => {
  // Extract year from mktCostUpdateTime (format: "2026-01")
  // Default to '25' if no valid year is found
  const yearPrefix = mktCostUpdateTime && mktCostUpdateTime.length >= 4
    ? mktCostUpdateTime.substring(2, 4)
    : '25';

  return [
    {
      header: <div style={{ fontWeight: 700 }}>{formatMessage({ id: 'dbs_project' })}</div>,
      dataIndex: 'Project',
      key: 'Project',
      width: 120,
      render: (cvm: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any, stableUpdater: (value: any) => void, setEditData: any, validationErrors: Record<string, string[]>) => {
        // 判断是否为一级（category）：使用 isCategory 标识
        const isCategory = cvm.isCategory === true;

        const onChange = (value: any) => {
          stableUpdater(value);
        }

        const onDeleteCategory = () => {
          // 通过stableUpdater传递特殊标记来表示删除
          stableUpdater({ __delete: true });
        }

        const onDeleteProject = async () => {
          // Delete handler for projects (not categories) with confirmation
          const confirmed = await Modal.confirm({
            className: 'dbs-delete-confirm-modal',
            message: formatMessage({ id: 'dbs_delete_project_confirm' }),
            okText: formatMessage({ id: 'dbs_confirm' }),
            cancelText: formatMessage({ id: 'dbs_cancel' }),
            size: 'auto'
          });

          if (confirmed) {
            stableUpdater({ __delete: true });
          }
        }

        // 判断是否有children（项目）
        const hasChildren = cvm.children && Array.isArray(cvm.children) && cvm.children.length > 0;

        // 配置删除图标：有children时置灰并显示提示，没有children时正常显示
        const showDeleteConfig = hasChildren
          ? { disabled: true, tooltip: formatMessage({ id: 'dbs_cannot_delete_category_while_contains_projects' }) }
          : { disabled: false };

        // 判断是否是新增行（id 以 'new_project_' 开头）
        const isNewRow = cvm.id && String(cvm.id).startsWith('new_project_');

        // 检查是否有错误（参考 Overview 的 editProjectColums）
        const recordId = String(cvm.id);
        const recordErrors = validationErrors[recordId] || [];
        const hasError = recordErrors.includes('Project');

        // Determine if this is a newly created regular project (new row, not a category)
        // Use isCategory as the definitive identifier - a category without children is still a category
        const isNewRegularProject = isNewRow && !isCategory;

        return (
          <span style={{ fontWeight: cvm.isCategory ? 700 : 400, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Category: Use PhaseCell */}
            {isCategory ? (
              <PhaseCell
                titleText={localizeLabel(cvm.Project, formatMessage)}
                onChange={onChange}
                onDelete={onDeleteCategory}
                showDelete={showDeleteConfig}
                formatMessage={formatMessage}
                isNewRow={isNewRow}
                hasError={hasError}
                deleteMessage={formatMessage({ id: 'dbs_delete_category_confirm' })}
              />
            ) : isNewRegularProject ? (
              /* Newly Created Regular Project: Use FollowerRange with DeleteButton */
              <>
                <FollowerRange
                  className={`dbs-follower-range dbs-project-title-select ${hasError ? 'ds-timeline-error' : ''}`}
                  overlayClassName="dbs-follower-range"
                  value={(() => {
                    // Get the current project value (could be text or value)
                    const projectValue = cvm.Project || '';
                    if (!projectValue) return '';

                    // Try to find matching option by text
                    const matchingOption = projectOptions.find((opt: any) => opt.text === projectValue);
                    if (matchingOption) {
                      return matchingOption.value;
                    }

                    // If no match, it's a custom value, return as-is
                    return projectValue;
                  })()}
                  onChange={(value: any) => {
                    // Find matching option
                    const selectedOption = projectOptions.find((opt: any) => opt.value === value);
                    if (selectedOption) {
                      // Selected from list: use text
                      stableUpdater(selectedOption.text);
                    } else {
                      // Custom input: use value directly
                      stableUpdater(value);
                    }
                  }}
                  customizePlaceholder={formatMessage({ id: 'dbs_please_input_project_name' })}
                  options={projectOptions}
                  searchable
                  searchPlaceholder="Search project"
                  isCustomize={true}
                  appearance="default"
                  size="auto"
                  boxStyle={{ width: '200px' }}
                  loading={loading}
                  // onSearch={onProjectSearch}
                  placeholder={loading ? 'Loading projects...' : 'Select Project'}
                  placeholderStyle={{ fontWeight: 400 }}
                />
                <DeleteButton
                  onClick={onDeleteProject}
                  title={formatMessage({ id: 'dbs_delete' })}
                />
              </>
            ) : (
              /* Existing Project: display text with DeleteButton */
              <>
                <span>{localizeLabel(cvm.Project, formatMessage)}</span>
                <DeleteButton
                  onClick={onDeleteProject}
                  title={formatMessage({ id: 'dbs_delete' })}
                />
              </>
            )}
          </span>
        );
      }
    },
    {
      header: rendTitle({ title: `${yearPrefix}A`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
      dataIndex: '25A',
      key: '25A',
      width: 70,
      render: (cvm: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any, stableUpdater: (value: any) => void) => {
        // 判断是否为二级目录（使用 isCategory 标识）
        const isProject = cvm.isCategory === false;

        if (isProject) {
          // 二级目录：显示可编辑的输入框
          const onChange = (value: string) => {
            const trimmedValue = value.trim();
            const trimmedLower = trimmedValue.toLowerCase();

            // Special case: "NA" or "N/A" (case-insensitive) → null
            if (trimmedLower === 'na' || trimmedLower === 'n/a') {
              stableUpdater(null);
              return;
            }

            // Special case: "-" or empty → 0
            if (trimmedValue === '' || trimmedValue === '-') {
              stableUpdater(0);
              return;
            }

            // Try to convert to number
            const numValue = parseFloat(trimmedValue);

            // If valid number, send the value (multiply by 1000000 to convert back to original unit)
            if (!isNaN(numValue)) {
              const finalValue = numValue * 1000000;
              stableUpdater(finalValue);
            } else {
              // If invalid input (like "dede", "abc") → null
              stableUpdater(null);
            }
          };

          const calculatedInitialData = cvm['25A'] !== null && cvm['25A'] !== undefined ? Math.round((cvm['25A'] / 1000000) * 100) / 100 : null;

          return (
            <InputNumberComponent
              initialData={calculatedInitialData}
              onChange={onChange}
            />
          );
        } else {
          // 一级目录：只显示文本
          return <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{toMillions(cvm?.['25A'])}</div>;
        }
      }
    },
    {
      header: rendTitle({ title: `${yearPrefix}F`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
      dataIndex: '25F',
      key: '25F',
      width: 70,
      render: (cvm: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any, stableUpdater: (value: any) => void) => {
        // 判断是否为二级目录（使用 isCategory 标识）
        const isProject = cvm.isCategory === false;

        if (isProject) {
          // 二级目录：显示可编辑的输入框
          const onChange = (value: string) => {
            const trimmedValue = value.trim();
            const trimmedLower = trimmedValue.toLowerCase();

            // Special case: "NA" or "N/A" (case-insensitive) → null
            if (trimmedLower === 'na' || trimmedLower === 'n/a') {
              stableUpdater(null);
              return;
            }

            // Special case: "-" or empty → 0
            if (trimmedValue === '' || trimmedValue === '-') {
              stableUpdater(0);
              return;
            }

            // Try to convert to number
            const numValue = parseFloat(trimmedValue);

            // If valid number, send the value (multiply by 1000000 to convert back to original unit)
            if (!isNaN(numValue)) {
              const finalValue = numValue * 1000000;
              stableUpdater(finalValue);
            } else {
              // If invalid input (like "dede", "abc") → null
              stableUpdater(null);
            }
          };

          const calculatedInitialData = cvm['25F'] !== null && cvm['25F'] !== undefined ? Math.round((cvm['25F'] / 1000000) * 100) / 100 : null;

          return (
            <InputNumberComponent
              initialData={calculatedInitialData}
              onChange={onChange}
            />
          );
        } else {
          // 一级目录：只显示文本
          return <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{toMillions(cvm?.['25F'])}</div>;
        }
      }
    },
    {
      header: rendTitle({ title: `${yearPrefix}B`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
      dataIndex: '25B',
      key: '25B',
      width: 70,
      render: (cvm: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any, stableUpdater: (value: any) => void) => {
        // 判断是否为二级目录（使用 isCategory 标识）
        const isProject = cvm.isCategory === false;

        if (isProject) {
          // 二级目录：显示可编辑的输入框
          const onChange = (value: string) => {
            const trimmedValue = value.trim();
            const trimmedLower = trimmedValue.toLowerCase();

            // Special case: "NA" or "N/A" (case-insensitive) → null
            if (trimmedLower === 'na' || trimmedLower === 'n/a') {
              stableUpdater(null);
              return;
            }

            // Special case: "-" or empty → 0
            if (trimmedValue === '' || trimmedValue === '-') {
              stableUpdater(0);
              return;
            }

            // Try to convert to number
            const numValue = parseFloat(trimmedValue);

            // If valid number, send the value (multiply by 1000000 to convert back to original unit)
            if (!isNaN(numValue)) {
              const finalValue = numValue * 1000000;
              stableUpdater(finalValue);
            } else {
              // If invalid input (like "dede", "abc") → null
              stableUpdater(null);
            }
          };

          const calculatedInitialData = cvm['25B'] !== null && cvm['25B'] !== undefined ? Math.round((cvm['25B'] / 1000000) * 100) / 100 : null;

          return (
            <InputNumberComponent
              initialData={calculatedInitialData}
              onChange={onChange}
            />
          );
        } else {
          // 一级目录：只显示文本
          return <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{toMillions(cvm?.['25B'])}</div>;
        }
      }
    },
    {
      header: (
        <CommonBubble content={formatMessage({ id: 'dbs_mkt_25A_25B' })}>
          <div style={{ textAlign: 'center', textWrap: 'nowrap' }}>{yearPrefix}A / {yearPrefix}B</div>
        </CommonBubble>
      ),
      dataIndex: '25A VS 25B2',
      key: '25A VS 25B2',
      width: 140,
      render: (cvm: any) => {
        try {
          // Handle 0/0 case to avoid NaN
          const valueA = cvm?.['25A'] || 0;
          const valueB = cvm?.['25B'] || 0;

          // If both are 0, or B is 0, show 0%
          const dValue = valueB === 0 ? 0 : (valueA / valueB * 100);
          const newArr: ICapsuleData[] = getTableCapsuleConfig({ value: dValue, maxValue: maxVsVallue });
          return <div className={`${dValue === 0 ? 'dbs-table-default-gay' : cvm?.['Sustainability']} dbs-table-capsule dbs-table-capsule-positive`}
            style={{ marginLeft: dValue === 0 ? '26px' : '0' }}>
            <SpecialTableBar data={newArr} height={20} coefficient={1.5} />
          </div>
        } catch (error) {
          console.error(error);
        }
      }
    },
    {
      header: <CommonBubble content={formatMessage({ id: 'dbs_mkt_sustainability' })}>{formatMessage({ id: 'dbs_table_sustainability' })}</CommonBubble>,
      dataIndex: 'Sustainability',
      key: 'Sustainability',
      width: 130,
      render: (cvm: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any, stableUpdater: (value: any) => void) => {
        // 判断是否为二级目录（项目）
        const isProject = !cvm.isCategory;

        // 如果是项目，显示下拉选择；如果是分类，显示标签
        return (
          <SustainabilitySelect
            initialValue={cvm?.['Sustainability'] || ''}
            isCategory={false}
            onChange={(value: string) => {
              stableUpdater(value);
            }}
          />
        );
      },
    },
  ];
};

